import { api } from '@api'
import { Constants } from '@hvf/contracts'
import dayjs from 'dayjs'
import type { SearchCategory } from './categories'

export type FilterOption = { value: string; label: string }

/**
 * One filter in the search panel, mirroring the filters on that category's own page.
 *  - `single`: pick one; the option with value '' means "any"
 *  - `multi`: pick several; nothing picked means "any"
 *  - `search`: type to find values (symbols, exchanges, members), pick several
 * Options are either fixed or loaded once from the database (`load`).
 */
export type FilterDef = {
  key: string
  label: string
  type: 'single' | 'multi' | 'search'
  options?: FilterOption[]
  load?: () => Promise<FilterOption[]>
  find?: (term: string) => Promise<FilterOption[]>
  placeholder?: string
}

export type FilterValues = Record<string, string | string[]>

const pretty = (s: string) => s.replaceAll('_', ' ').replaceAll('-', ' ').replace(/^./, (c) => c.toUpperCase())
const opts = (values: readonly string[]) => values.map((v) => ({ value: v, label: pretty(v) }))

/** Labels for values picked through `search` filters (e.g. member id → username), so chips can show names */
export const optionLabels = new Map<string, string>()
const remember = (options: FilterOption[]) => {
  for (const o of options) optionLabels.set(o.value, o.label)
  return options
}

const findSetupValues = (column: 'symbol' | 'exchange') => async (term: string) => {
  const { data } = await api()
    .from('setup')
    .select(column)
    .ilike(column, `%${term}%`)
    .not(column, 'is', null)
    .limit(50)
  const unique = [...new Set((data ?? []).map((r) => (r as Record<string, string>)[column]).filter(Boolean))]
  return remember(unique.slice(0, 8).map((v) => ({ value: v, label: v })))
}

const findMembers = async (term: string) => {
  const { data } = await api().from('profile').select('id, username').ilike('username', `%${term}%`).limit(8)
  return remember((data ?? []).filter((p) => p.username).map((p) => ({ value: p.id, label: `@${p.username}` })))
}

const loadCourses = async () => {
  const { data } = await api().rpc('get_courses_with_progress')
  return (data ?? [])
    .map((c) => ({ value: c.id, label: c.title }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

const loadEventGroups = async () => {
  const { data } = await api().from('events').select('group')
  const groups = [...new Set((data ?? []).map((e) => e.group).filter((g): g is string => !!g))].sort()
  return groups.map((g) => ({ value: g, label: g }))
}

const loadRecordingGroups = async () => {
  const { data } = await api().rpc('get_recording_discovery_rows')
  const names = [...new Set((data ?? []).map((r) => r.group_name))]
  // Same wording as the Recordings page
  return names.map((n) => ({ value: n, label: n === 'General' ? 'Uncategorized' : n }))
}

const AUTHOR: FilterDef = {
  key: 'author',
  label: 'Author',
  type: 'search',
  find: findMembers,
  placeholder: 'Search members…',
}

const PERIOD: FilterDef = {
  key: 'period',
  label: 'Date',
  type: 'single',
  options: [
    { value: '', label: 'Any time' },
    { value: '7', label: 'Past week' },
    { value: '30', label: 'Past month' },
    { value: '90', label: 'Past 3 months' },
    { value: '365', label: 'Past year' },
  ],
}

const TOPIC: FilterDef = {
  key: 'topic',
  label: 'Topic',
  type: 'single',
  options: [{ value: '', label: 'All' }, ...opts(Constants.public.Enums.go_read_topic)],
}

export const CATEGORY_FILTERS: Record<SearchCategory, FilterDef[]> = {
  setup: [
    AUTHOR,
    { key: 'symbol', label: 'Symbol', type: 'search', find: findSetupValues('symbol'), placeholder: 'Search symbols…' },
    { key: 'exchange', label: 'Exchange', type: 'search', find: findSetupValues('exchange'), placeholder: 'Search exchanges…' },
    { key: 'market', label: 'Market', type: 'multi', options: opts(Constants.public.Enums.market) },
    {
      key: 'position',
      label: 'Position',
      type: 'single',
      options: [{ value: '', label: 'Any' }, ...opts(Constants.public.Enums.direction)],
    },
    { key: 'stage', label: 'Stage', type: 'multi', options: opts(Constants.public.Enums.stage) },
    {
      key: 'pattern',
      label: 'Pattern',
      type: 'multi',
      options: [...opts(Constants.public.Enums.pattern_specification), { value: 'unspecified', label: 'Unspecified' }],
    },
    {
      key: 'duration',
      label: 'Duration',
      type: 'single',
      options: [{ value: '', label: 'Any' }, ...opts(Constants.public.Enums.duration)],
    },
  ],
  chat: [
    AUTHOR,
    {
      key: 'channel',
      label: 'Channel',
      type: 'single',
      options: [
        { value: '', label: 'All' },
        { value: 'general', label: 'Main channels' },
        { value: 'setup', label: 'Setup chats' },
      ],
    },
    PERIOD,
  ],
  course: [
    { key: 'course', label: 'Course', type: 'single', load: async () => [{ value: '', label: 'All courses' }, ...(await loadCourses())] },
    {
      key: 'progress',
      label: 'Progress',
      type: 'single',
      options: [
        { value: '', label: 'Any' },
        { value: 'not-started', label: 'Not started' },
        { value: 'in-progress', label: 'In progress' },
        { value: 'completed', label: 'Completed' },
      ],
    },
    { key: 'lesson_type', label: 'Lesson type', type: 'multi', options: opts(Constants.public.Enums.lesson_type) },
  ],
  event: [
    { key: 'group', label: 'Group', type: 'multi', load: loadEventGroups },
    {
      key: 'schedule',
      label: 'Schedule',
      type: 'single',
      options: [
        { value: '', label: 'Any' },
        { value: 'one-time', label: 'One-time' },
        { value: 'recurring', label: 'Recurring' },
      ],
    },
  ],
  recording: [
    { key: 'group', label: 'Group', type: 'multi', load: loadRecordingGroups },
    PERIOD,
    {
      key: 'evergreen',
      label: 'Evergreen',
      type: 'single',
      options: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Evergreen only' },
      ],
    },
  ],
  go_read: [AUTHOR, TOPIC],
  go_watch: [
    AUTHOR,
    TOPIC,
    {
      key: 'provider',
      label: 'Source',
      type: 'multi',
      options: [
        { value: 'youtube', label: 'YouTube' },
        { value: 'vimeo', label: 'Vimeo' },
        { value: 'dailymotion', label: 'Dailymotion' },
      ],
    },
  ],
  member: [
    {
      key: 'role',
      label: 'Rank',
      type: 'multi',
      // Top Gun is stored as a flag beside the rank, but reads as a rank to members
      options: [{ value: 'top_gun', label: 'Top Gun' }, ...opts(Constants.public.Enums.role)],
    },
  ],
}

const isSet = (v: string | string[] | undefined) => (Array.isArray(v) ? v.length > 0 : !!v)

export const countActive = (values: FilterValues | undefined) =>
  Object.values(values ?? {}).filter(isSet).length

/** Converts panel values to the `p_filters` payload the global_search RPC expects */
export const toPayload = (values: FilterValues | undefined): Record<string, unknown> => {
  const payload: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(values ?? {})) {
    if (!isSet(value)) continue
    if (key === 'period') payload.since = dayjs().subtract(Number(value), 'day').format('YYYY-MM-DD')
    else if (value === 'true') payload[key] = true
    else payload[key] = value
  }
  return payload
}
